import React from "react";
import { PageHeader } from "@/components/ui-blocks/PageHeader";
import { FileDown, ShieldCheck, ScrollText, CheckCircle2 } from "lucide-react";
import { TransparencyDashboard } from "@/components/sections/TransparencyDashboard";
import { SEO } from "@/lib/seo";

export default function TransparencyPage() {
  return (
    <main>
      <SEO title="Transparency Dashboard" description="Live donation intent, spend split, and audited financial breakdown." breadcrumbs={[{name:"Home", href:"/"},{name:"Transparency", href:"/transparency"}]} />
      <PageHeader eyebrow="Transparency" title="Every number, every rupee, on the record." lead="We publish an annual audited transparency report, quarterly impact briefs and a live donation ledger." />
      <TransparencyDashboard />
      <section className="container-wide py-14 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="rounded-2xl border border-border p-8 bg-background">
            <div className="eyebrow mb-3">Planned Allocation — FY 2026-27</div>
            <div className="grid gap-3">
              {[["Programs & Beneficiaries", 93, "hsl(var(--orange))"], ["Governance & Compliance", 4, "hsl(var(--green))"], ["Fundraising & Communications", 3, "hsl(var(--foreground))"]].map(([label, pct, color]) => (
                <div key={label}>
                  <div className="flex items-center justify-between text-sm"><span className="text-foreground font-semibold">{label}</span><span className="text-[hsl(var(--muted-ink))]">{pct}%</span></div>
                  <div className="h-2 mt-1 rounded-full bg-[hsl(var(--surface-tint))] overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, background: color }} /></div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[hsl(var(--muted-ink))] mt-4">CAF was incorporated in 2026. Our first statutory audit will be published at the end of FY 2026-27. Queries: <a className="underline" href="mailto:transparency@caf.org.in">transparency@caf.org.in</a>.</p>
          </div>

          <div className="mt-8 rounded-2xl border border-border p-8 bg-background">
            <div className="eyebrow mb-3">Annual Reports</div>
            <div className="rounded-xl border border-dashed border-border p-8 text-center" data-testid="annual-reports-founding-note">
              <ScrollText className="h-8 w-8 mx-auto text-[hsl(var(--green))]" />
              <div className="mt-3 font-semibold text-foreground">Founding year — 2026</div>
              <p className="text-sm text-[hsl(var(--muted-ink))] mt-2 max-w-md mx-auto">Citizen Awareness Foundation was incorporated in 2026 as a Section 8 company. Our first audited annual report (FY 2026-27) will be published here. Until then, our live donation ledger above stays open — from day one.</p>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <div className="rounded-2xl border border-border p-6 bg-[hsl(var(--surface-tint))]">
            <div className="eyebrow">Compliance</div>
            <ul className="mt-3 text-sm space-y-2">
              {["Section 8 Company — Companies Act, 2013", "12A registration", "80G registration", "CSR-1 registration", "FCRA — in progress"].map((t) => (<li key={t} className="flex items-center gap-2 text-foreground"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--green))]" /> {t}</li>))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border p-6 bg-background">
            <div className="eyebrow">Impact methodology</div>
            <p className="text-sm text-[hsl(var(--muted-ink))] mt-2">Beneficiary counts are de-duplicated across programs, verified via ID-linked attendance registers, and independently sampled for accuracy.</p>
            <div className="mt-3"><ShieldCheck className="h-5 w-5 text-[hsl(var(--green))]" /></div>
          </div>
        </aside>
      </section>
    </main>
  );
}
